import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { API_BASE_URL } from "../../../config/api";
import { categoryTypeService } from "../../../services/categoryTypeService";
import StyledCategoryTypeDropdown from "./StyledCategoryTypeDropdown";
import CategoryTypePopup from "./CategoryTypePopup";

const EditCategoryModal = ({ category, onClose, onSave }) => {
  const [categoryName, setCategoryName] = useState(category.categoryName);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(category.categotyImage);
  const [categoryTypeId, setCategoryTypeId] = useState(
    category.categoryTypeId || ""
  );
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [showCategoryTypePopup, setShowCategoryTypePopup] = useState(false);
  const [categoryDescription, setCategoryDescription] = useState(category.categoryDescription || "");
  const [selectedBannerImage, setSelectedBannerImage] = useState(null);
  const [previewBannerImage, setPreviewBannerImage] = useState(category.categoryBannerImage || null);

  useEffect(() => {
    fetchCategoryTypes();
  }, []);

  const fetchCategoryTypes = async () => {
    try {
      const data = await categoryTypeService.getAllCategoryTypes();
      setCategoryTypes(data);
    } catch (error) {
      console.error("Failed to fetch category types:", error);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        setSelectedImage(file);
        const imageUrl = URL.createObjectURL(file);
        setPreviewImage(imageUrl);
      } else {
        alert("Please select an image file");
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        setSelectedImage(file);
        const imageUrl = URL.createObjectURL(file);
        setPreviewImage(imageUrl);
      } else {
        alert("Please drop an image file");
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSave = () => {
    if (!categoryName.trim()) {
      alert("Please enter a category name");
      return;
    }

    if (!categoryTypeId) {
      alert("Please select a category type");
      return;
    }

    // Find the selected category type to get its name
    const selectedType = categoryTypes.find(
      (type) => type._id === categoryTypeId
    );
    if (!selectedType) {
      alert("Selected category type not found");
      return;
    }

    onSave(categoryName, selectedImage, categoryTypeId, selectedType.typeName, categoryDescription, selectedBannerImage || previewBannerImage);
  };

  const handleAddCategoryType = async (newType) => {
    await fetchCategoryTypes();
    setCategoryTypeId(newType._id);
  };

  const handleCategoryTypeDeleted = async () => {
    await fetchCategoryTypes();
    setCategoryTypeId("");
  };

  const handleCategoryTypeChange = (newTypeId) => {
    setCategoryTypeId(newTypeId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Edit Category</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        {/* Image Upload Area */}
        <div
          className="border-2 border-dashed border-gray-300 rounded-lg mb-4 p-4"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {previewImage ? (
            <div className="relative">
              <img
                src={previewImage}
                alt="Category"
                className="w-full h-48 object-cover rounded-lg"
              />
              <button
                onClick={() => {
                  setSelectedImage(null);
                  setPreviewImage(category.categotyImage); // Reset to original image
                }}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                ×
              </button>
            </div>
          ) : (
            <div className="h-48 flex flex-col items-center justify-center text-gray-400">
              <p>Drop your image here or</p>
              <label className="text-purple-600 cursor-pointer hover:text-purple-700">
                Browse
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleImageSelect}
                />
              </label>
            </div>
          )}
        </div>

        {/* Category Name Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Category Name
          </label>
          <input
            type="text"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
          />
        </div>

        {/* Category Description (Optional) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-gray-400 font-normal">(Optional)</span>
          </label>
          <textarea
            placeholder="Enter category description..."
            value={categoryDescription}
            onChange={(e) => setCategoryDescription(e.target.value)}
            className="w-full px-3 py-2 border rounded-md resize-vertical"
            rows={3}
          />
        </div>

        {/* Banner Image (Optional) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Banner Image <span className="text-gray-400 font-normal">(Optional)</span>
          </label>
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-3"
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files[0];
              if (file && file.type.startsWith("image/")) {
                setSelectedBannerImage(file);
                setPreviewBannerImage(URL.createObjectURL(file));
              }
            }}
            onDragOver={(e) => e.preventDefault()}
          >
            {previewBannerImage ? (
              <div className="relative">
                <img
                  src={typeof previewBannerImage === 'string' && previewBannerImage.startsWith('blob:') ? previewBannerImage : (previewBannerImage.startsWith('http') ? previewBannerImage : `${API_BASE_URL}${previewBannerImage}`)}
                  alt="Banner preview"
                  className="w-full h-32 object-cover rounded-lg"
                />
                <button
                  onClick={() => {
                    setSelectedBannerImage(null);
                    setPreviewBannerImage(null);
                  }}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 text-xs"
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="h-20 flex flex-col items-center justify-center text-gray-400 text-sm">
                <p>Drop banner image here or</p>
                <label className="text-purple-600 cursor-pointer hover:text-purple-700">
                  Browse
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file && file.type.startsWith("image/")) {
                        setSelectedBannerImage(file);
                        setPreviewBannerImage(URL.createObjectURL(file));
                      }
                    }}
                  />
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Category Type Dropdown */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Category Type
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <StyledCategoryTypeDropdown
                types={categoryTypes}
                value={categoryTypeId}
                onChange={handleCategoryTypeChange}
                onCategoryTypeDeleted={handleCategoryTypeDeleted}
              />
            </div>
            <button
              type="button"
              onClick={() => setShowCategoryTypePopup(true)}
              className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 whitespace-nowrap"
            >
              Add New
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!categoryName.trim() || !categoryTypeId}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* Category Type Popup */}
      {showCategoryTypePopup && (
        <CategoryTypePopup
          onClose={() => setShowCategoryTypePopup(false)}
          onAdd={handleAddCategoryType}
        />
      )}
    </div>
  );
};

export default EditCategoryModal;
